import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid";
import { chemicalExpressionFromConstraintCoefficients } from "components/constraints/chemicalExpressionFromConstraintCoefficients";
import { useTenantData } from "contexts/tenant";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useMemo } from "react";
import {
  ChemicalConstraint,
  ChemicalElement,
  ImpurityUnit,
} from "src/store/api/generatedApi";

interface Props {
  chemicalConstraints: ChemicalConstraint[];
  elements: Record<number, ChemicalElement>;
}

export const ChemicalTolerancesTable = ({
  chemicalConstraints,
  elements,
}: Props) => {
  const { t } = useTenantTranslation();

  const tenantData = useTenantData();
  const units = useUnitsFormatter(true);

  const constraintRows = useConstraintRows(chemicalConstraints, elements);
  const constraintColumns: GridColDef<ConstraintRow>[] = useMemo(
    () => [
      { field: "expression", headerName: t("constraint"), flex: 1 },
      {
        field: "min",
        headerName: `${t("min")} ${units(tenantData.impurity_units)}`,
        width: 110,
        valueFormatter: (value) =>
          formatImpurity(
            value,
            tenantData.impurity_units,
            tenantData.small_mass_per_mass
          ),
      },
      {
        field: "max",
        headerName: `${t("max")} ${units(tenantData.impurity_units)}`,
        width: 110,
        valueFormatter: (value) =>
          formatImpurity(
            value,
            tenantData.impurity_units,
            tenantData.small_mass_per_mass
          ),
      },
    ],
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- tenantData, units, and formatImpurity are stable across renders; only t (language change) requires column rebuild
    [t]
  );

  return (
    <HotkeyCompliantDataGrid
      rows={constraintRows}
      columns={constraintColumns}
      getRowId={(row) => row.expression}
      density="compact"
      hideFooter
      disableColumnMenu
      cellSelection
    />
  );
};

interface ConstraintRow {
  expression: string;
  min: number | null;
  max: number;
}

const formatImpurity = (
  value: number | null,
  impurityUnits: ImpurityUnit,
  conversionFactor: number
): string => {
  if (value == null) {
    return "-";
  }
  switch (impurityUnits) {
    case "mass_fraction":
      return value.toString();
    case "small_mass_fraction":
      return (value * conversionFactor).toString();
  }
};

const useConstraintRows = (
  chemicalConstraints: ChemicalConstraint[],
  elements: Record<number, ChemicalElement>
): ConstraintRow[] => {
  return useMemo(
    () =>
      chemicalConstraints.map(
        ({ name, coefficients, weight_percent_min, weight_percent_max }) => ({
          expression: chemicalExpressionFromConstraintCoefficients(
            name,
            coefficients,
            elements
          ),
          min: weight_percent_min ?? null,
          max: weight_percent_max,
        })
      ),
    [chemicalConstraints, elements]
  );
};
